import React, {
  memo,
  forwardRef,
  useImperativeHandle,
  useState,
  useEffect,
} from 'react';

import { useDisclosure, useToast } from '@chakra-ui/react';

import {
  createImpactReport,
  fetchCollectionPoints,
  fetchHarvestSummarys,
} from 'api/reports';
import { CreateImpactReportPayload } from 'types/report';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import { RefType, AddImpactReportProps } from './AddImpactReport.props';

import AddImpactReportView from './AddImpactReport.view';
import { InventoryReportStatusEnum } from 'types/report';
import { washHub } from 'store';
import useQueryParams from 'hooks/useQueryParams';

const Container = forwardRef<RefType, AddImpactReportProps>((props, ref) => {
  const [site, setSite] = useState<string | undefined | null>();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [reportId, setReportId] = useState<string | undefined | null>('');

  const isWashHubIdValid = washHub.selectors.useIsWashHubIdValid();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isValueFromHarvest, setIsValueFromHarvest] = useState<boolean | null>(
    null,
  );

  const queryUrl = useQueryParams();

  useImperativeHandle(
    ref,
    () => ({
      onOpen: (reportId, siteId) => {
        if (reportId) {
          setIsValueFromHarvest(true);
        } else {
          setIsValueFromHarvest(false);
        }
        setReportId(reportId);
        setSite(siteId);
        onOpen();
      },
    }),
    [],
  );

  // Get site ID from query parameters as fallback
  useEffect(() => {
    const { siteId: siteFromUrl } = queryUrl;
    if (siteFromUrl) {
      setSite(siteFromUrl);
    }
  }, [queryUrl]);

  // Debug: Track site state changes
  useEffect(() => {
    console.log('AddImpactReport site state changed to:', site);
  }, [site]);
  const summaryStatus: InventoryReportStatusEnum[] = [
    InventoryReportStatusEnum.READY_FOR_IMPACT,
    InventoryReportStatusEnum.READY_FOR_INVOICE,
    InventoryReportStatusEnum.DONE,
  ];

  const washHubId = washHub.selectors.useSelectedWashHubId();

  const queryFnCollectionPoints = () => fetchCollectionPoints(washHubId);
  const options = {
    enabled: isWashHubIdValid,
    keepPreviousData: true,
  };
  const {
    data: dataCollectionReport,
    isLoading: isLoadingCollectionReports,
  } = useQuery(
    [`fetch-collectionPoints-report`, washHubId],
    queryFnCollectionPoints,
    options,
  );

  const queryFn = () => {
    const queryParams = {
      site: site ? [site] : [],
      limit: 100,
      generateImpact: true,
      status: summaryStatus,
    };
    return fetchHarvestSummarys(queryParams);
  };
  const { data, isLoading: isLoadingReports } = useQuery(
    [`fetch-impact-reports`, site],
    queryFn,
    {
      enabled: site !== undefined && site !== null && site !== '',
      keepPreviousData: true,
    },
  );

  const { mutate, isLoading } = useMutation(createImpactReport, {
    onSuccess: () => {
      toast({
        status: 'success',
        title: 'Success',
        description: 'Impact Report Created',
        isClosable: true,
        duration: 5000,
      });

      queryClient.invalidateQueries({
        queryKey: ['fetch-impact-reports'],
      });

      queryClient.invalidateQueries({
        queryKey: [`fetch-inventory-reports`],
      });
      queryClient.invalidateQueries({
        queryKey: [`fetch-reports`],
      });

      // Invalidate all activity history queries since new reports are created
      queryClient.invalidateQueries({
        predicate: (query) => {
          const queryKey = query.queryKey;
          return (
            Array.isArray(queryKey) &&
            queryKey.length > 0 &&
            queryKey[0].includes('fetch-report-activity-history')
          );
        },
      });

      onClose();
    },
  });

  let inventoryReports =
    data?.items.sort(function (a, b) {
      if (a.name < b.name) {
        return -1;
      }
      if (a.name > b.name) {
        return 1;
      }
      return 0;
    }) || [];

  const changeSiteName = (value: string) => {
    setSite(value);
  };
  return (
    <AddImpactReportView
      isValueFromHarvest={isValueFromHarvest}
      reportId={reportId}
      isLoading={isLoading}
      isOpen={isOpen}
      onClose={onClose}
      setSiteName={changeSiteName}
      collectionPointsData={dataCollectionReport}
      isLoadingCollection={isLoadingCollectionReports}
      onSubmit={(payload: any) => {
        const co2m = payload?.co2m.replace(/,/g, '') || 0;
        const cos = payload?.cos.replace(/,/g, '') || 0;
        const er = payload?.er.replace(/,/g, '') || 0;
        const wc = payload?.wc.replace(/,/g, '') || 0;
        const wd = payload?.wd.replace(/,/g, '') || 0;
        mutate({
          co2m: Number(co2m || '0'),
          cos: Number(cos || '0'),
          er: Number(er || '0'),
          wc: Number(wc || '0'),
          wd: Number(wd || '0'),
          name: payload?.name || '',
          id: payload?.id || '',
          startDate: new Date(payload?.startDate) as any,
          endDate: new Date(payload?.endDate) as any,
          month: Number(payload?.month || '1'),
          year: Number(payload?.year || '2023'),
          single_use_items: Number(payload?.single_use_items || '0'),
        } as CreateImpactReportPayload);
      }}
      isLoadingReports={isLoadingReports}
      inventoryReports={site !== '' ? inventoryReports : []}
    />
  );
});

const AddImpactReport = memo(Container);

export default AddImpactReport;
